import { useForm } from "@tanstack/react-form";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useCreateMerchantStore } from "@/features/merchant/api/use-create-merchant-store";
import { useUpdateMerchantStore } from "@/features/merchant/api/use-update-merchant-store";
import type { MerchantStoreDto } from "@/features/merchant/api/dto";
import { useMerchantTranslation } from "@/features/merchant/i18n/use-merchant-translation";
import {
  createMerchantStoreSchema,
  type MerchantStoreFormValues,
} from "@/features/merchant/schemas/merchant-store.schema";
import { STORE_STATUSES } from "@/features/shared/api/enums";

const defaultValues = (
  merchantId: string,
  store?: MerchantStoreDto
): MerchantStoreFormValues => ({
  merchantId: store?.merchantId ?? merchantId,
  name: store?.name ?? "",
  address: store?.address ?? "",
  phone: store?.phone ?? "",
  gpsLat: store?.gpsLat ?? undefined,
  gpsLng: store?.gpsLng ?? undefined,
  status: store?.status ?? "ACTIVE",
});

/**
 * Formulaire de création / édition d'un point de vente. Pas d'argent en
 * jeu : pas de `ShareablePreview`.
 */
export function MerchantStoreFormDialog({
  open,
  onOpenChange,
  merchantId,
  store,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  merchantId: string;
  /** Fourni pour l'édition. */
  store?: MerchantStoreDto;
}) {
  const { t } = useTypedTranslation();
  const { t: tm } = useMerchantTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : dépendance voulue.
  const schema = useMemo(() => createMerchantStoreSchema(), [i18n.language]);

  const createStore = useCreateMerchantStore();
  const updateStore = useUpdateMerchantStore();
  const isEdit = Boolean(store?.id);
  const pending = isEdit ? updateStore.isPending : createStore.isPending;

  const form = useForm({
    defaultValues: defaultValues(merchantId, store),
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      const payload: MerchantStoreDto = {
        id: store?.id,
        merchantId: parsed.data.merchantId,
        name: parsed.data.name.trim(),
        address: parsed.data.address?.trim() || undefined,
        phone: parsed.data.phone?.trim() || undefined,
        gpsLat:
          typeof parsed.data.gpsLat === "number" && !Number.isNaN(parsed.data.gpsLat)
            ? parsed.data.gpsLat
            : undefined,
        gpsLng:
          typeof parsed.data.gpsLng === "number" && !Number.isNaN(parsed.data.gpsLng)
            ? parsed.data.gpsLng
            : undefined,
        status: parsed.data.status,
      };
      if (isEdit && store?.id) {
        updateStore.mutate(
          { storeId: store.id, body: payload },
          {
            onSuccess: () => {
              notifSilence(tm("merchant.stores.feedback.updated"));
              onOpenChange(false);
            },
            onError: (error) => notifSilenceError(error.message),
          }
        );
      } else {
        createStore.mutate(payload, {
          onSuccess: () => {
            notifSilence(tm("merchant.stores.feedback.created"));
            onOpenChange(false);
          },
          onError: (error) => notifSilenceError(error.message),
        });
      }
    },
  });

  // Reset défensif à l'ouverture (Radix ne démonte pas systématiquement les
  // sous-arbres selon la config du Dialog).
  useEffect(() => {
    if (open) form.reset(defaultValues(merchantId, store));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, merchantId, store?.id]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? tm("merchant.stores.edit")
              : tm("merchant.stores.create")}
          </DialogTitle>
        </DialogHeader>
        <form
          className="grid grid-cols-1 gap-4 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.Field name="name">
            {(field) => (
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="store-name">
                  {tm("merchant.stores.form.name")}
                </Label>
                <Input
                  id="store-name"
                  value={field.state.value}
                  placeholder={tm("merchant.stores.form.namePlaceholder")}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
              </div>
            )}
          </form.Field>

          <form.Field name="address">
            {(field) => (
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="store-address">
                  {tm("merchant.stores.form.address")}
                </Label>
                <Input
                  id="store-address"
                  value={field.state.value ?? ""}
                  placeholder={tm("merchant.stores.form.addressPlaceholder")}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
              </div>
            )}
          </form.Field>

          <form.Field name="phone">
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="store-phone">
                  {tm("merchant.stores.form.phone")}
                </Label>
                <Input
                  id="store-phone"
                  className="notranslate"
                  value={field.state.value ?? ""}
                  placeholder={tm("merchant.stores.form.phonePlaceholder")}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
              </div>
            )}
          </form.Field>

          <form.Field name="status">
            {(field) => (
              <div className="space-y-1.5">
                <Label>{tm("merchant.stores.form.status")}</Label>
                <Select
                  value={field.state.value}
                  onValueChange={(v) =>
                    field.handleChange(v as MerchantStoreFormValues["status"])
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STORE_STATUSES.map((v) => (
                      <SelectItem key={v} value={v} className="notranslate">
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </form.Field>

          <form.Field name="gpsLat">
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="store-gps-lat">
                  {tm("merchant.stores.form.gpsLat")}
                </Label>
                <Input
                  id="store-gps-lat"
                  className="notranslate"
                  type="number"
                  step="any"
                  value={
                    typeof field.state.value === "number"
                      ? field.state.value
                      : ""
                  }
                  placeholder={tm("merchant.stores.form.gpsLatPlaceholder")}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.valueAsNumber)}
                />
              </div>
            )}
          </form.Field>

          <form.Field name="gpsLng">
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="store-gps-lng">
                  {tm("merchant.stores.form.gpsLng")}
                </Label>
                <Input
                  id="store-gps-lng"
                  className="notranslate"
                  type="number"
                  step="any"
                  value={
                    typeof field.state.value === "number"
                      ? field.state.value
                      : ""
                  }
                  placeholder={tm("merchant.stores.form.gpsLngPlaceholder")}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.valueAsNumber)}
                />
              </div>
            )}
          </form.Field>

          <DialogFooter className="sm:col-span-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={pending}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? <ButtonSpinner /> : null}
              {t("common.confirm")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
